import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { QRCodeSVG } from 'qrcode.react'
import { getSession, updateSession, getConfig, buildGalleryUrl } from '../utils/api.js'
import { Check, Plus, ArrowRight, LayoutGrid, Camera, Mail, QrCode } from 'lucide-react'
import './SessionSummary.css'

export default function SessionSummary() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [session, setSession] = useState(null)
  const [config, setConfig] = useState(null)
  const [email, setEmail] = useState('')
  const [saved, setSaved] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadData()
  }, [id])

  async function loadData() {
    try {
      const [sessionData, configData] = await Promise.all([
        getSession(id),
        getConfig()
      ])
      setSession(sessionData)
      setConfig(configData)
      setEmail(sessionData.email || '')
    } catch (err) {
      console.error('Failed to load session:', err)
      alert('Session not found')
      navigate('/')
    } finally {
      setLoading(false)
    }
  }

  async function saveEmail() {
    try {
      await updateSession(id, { email: email.trim() })
      setSaved(true)
      setTimeout(() => setSaved(false), 1800)
    } catch (err) {
      console.error('Failed to save email:', err)
    }
  }

  function addMoreShots() {
    // Navigate back to booth with session ID to continue
    navigate('/booth', { state: { continueSession: id } })
  }

  function nextFamily() {
    navigate('/booth')
  }

  function gotoAdmin() {
    navigate('/admin')
  }

  if (loading || !session) {
    return <div className="summary-screen"><div className="loading">Loading...</div></div>
  }

  const galleryUrl = buildGalleryUrl(config?.gallery_base_url, id)

  return (
    <div className="summary-screen">
      <div className="inner">
        <div className="summary-card">
          <div className="summary-header">
            <div className="session-title">
              <h2>{session.family_name}</h2>
              <span className="session-code">{session.id}</span>
            </div>
            <div className="qr-section">
              <QRCodeSVG value={galleryUrl} size={80} level="M" />
            </div>
          </div>

          <div className="photo-grid">
            {session.photos?.map(p => (
              <img
                key={p.id}
                src={p.public_url}
                alt="Captured photo"
                onClick={() => window.open(p.public_url, '_blank')}
              />
            ))}
          </div>

          <a
            className="gallery-link"
            href={galleryUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            📸 See all photos
          </a>

          <div className="divider" />

          <label className="field-label">Confirm Family Email</label>
          <div className="email-row">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="family@email.com"
            />
            <button className="btn" onClick={saveEmail}>
              {saved ? <Check size={16} /> : 'Save'}
            </button>
          </div>
        </div>

        <div className="actions-row">
          <button className="btn" onClick={addMoreShots}>
            <Plus size={16} /> More shots
          </button>
          <button className="btn btn-primary btn-large" onClick={nextFamily}>
            Next Family <ArrowRight size={18} />
          </button>
        </div>

        <div className="admin-link">
          <button className="btn btn-muted" onClick={gotoAdmin}>
            <LayoutGrid size={14} /> View all sessions
          </button>
        </div>
      </div>
    </div>
  )
}
